import '@/styles/global.css'
import '@/styles/customStyleInput.css'
import '@/styles/openTab.css'
import '@/styles/popup.css'
import '@/styles/attention.css'
import '@/styles/audio-outputs.css'
import '@/styles/ParticipantList.css'
import '@/styles/MediaPanel.css'
import '@/styles/globalsStyle.scss'
import '@/styles/menu.css'

import '@livekit/components-styles'
import '@livekit/components-styles/prefabs'

import { useRouter } from 'next/router'

import { useEffect } from 'react'
import { Provider } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ToastContainer, ToastIcon } from 'react-toastify'
import { parseCookie } from 'next/dist/compiled/@edge-runtime/cookies'

import jwt from 'jsonwebtoken'

import { Bell } from '@/src'
import { Seo } from '@/src/components/app/Seo/Seo'
import { MeetData } from '@/src/components/app/MeetData/MeetData'
import ErrorBoundary from '@/src/components/ErrorBoundary/ErrorBoundary'

import '@/src/i18n'

import { getCheckMeet } from '@/src/lib/server-utils'
import { fetchRoomByName, mapMeetClientRoomToBranding, mapMeetClientRoomToSettings, getRoomLookupErrorCode, roomLookupErrorKindFromCode } from '@/src/lib/meet-client-api'
import { RoomLookupErrorKind } from '@/src/lib/roomLookupError'
import { getClientPromptProps } from '@/src/lib/clientPrompt'

import { initialBranding } from '@/src/constants/branding'
import {
  isBitrixProjectMode,
  isSamoletProjectMode,
  resolveProjectMode,
} from '@/src/constants/projectModes'

import { Ctx, IPrompt, List, MyAppProps, Res } from '@/src/types/app.type'
import {
  ICustomTypeOfMeetResponse,
  ITypeOfMeet,
  ITypeOfMeetResponse,
} from '@/src/types/typeOfMeet.type'
import { IMeetCodes, ISettings } from '@/src/types/meetData.type'

import { store } from '@/src/store/rootReducer'

MyApp.getInitialProps = async ({ ctx }: Ctx) => {
  let brandingData: Res<List>
  let settingsData: ISettings | undefined
  let promptData: string | IPrompt = ''
  let typesOfMeet: ITypeOfMeet[] = []
  let meetCodes: IMeetCodes = { company_id: null, meet_id: null }
  // Почему не удалось загрузить комнату: PreJoin покажет оверлей вместо входа в звонок.
  let roomLookupError: RoomLookupErrorKind | null = null

  if (!ctx.req) {
    return {
      data: {},
      typesOfMeet: [],
      meetCodes,
      roomType: null,
      roomName: '',
      project: null,
      v: null,
      referrer: null,
      roomLookupError: null,
    }
  }

  const isMegaRoute =
    ctx.asPath?.startsWith('/mega/') ||
    ctx.pathname?.startsWith('/mega/') ||
    ctx.req?.url?.startsWith('/mega/')
  if (isMegaRoute || ctx.pathname === '/_error') {
    const megaUserName = typeof ctx.query?.user_name === 'string' ? ctx.query.user_name : null
    return {
      data: { ...initialBranding },
      typesOfMeet: [],
      meetCodes,
      roomType: null,
      roomName: '',
      project: null,
      v: null,
      referrer: null,
      prefilledUsername: megaUserName,
      hiddenButtons: { screenShare: false, chat: false, leave: false },
      roomLookupError: null,
    }
  }

  let path = `/${ctx.query.companySlug}/${ctx.query.name}`
  if (!ctx.query.name) {
    path = `/${ctx.query.companySlug}`
  }

  const roomNameFromHeaders = ctx.req?.headers['x-room-name'] as string
  const roomTypeFromHeaders = ctx.req?.headers['x-room-type'] as string
  const meetTokenFromHeaders = ctx.req?.headers['x-meet-token'] as string
  // Headers для короткой ссылки
  const projectFromHeaders = ctx.req?.headers['x-project'] as string
  const vFromHeaders = ctx.req?.headers['x-v'] as string
  const isolateFromHeaders = ctx.req?.headers['x-isolate'] as string

  const cookies = parseCookie(ctx?.req?.headers?.cookie || '')
  const cookieToken = cookies?.get(`meet_token:${path}`)
  const cookieRoomType = cookies.get('room_type')
  const cookieReferrer = cookies.get('x-referrer')

  const finalToken = meetTokenFromHeaders || cookieToken
  const finalRoomType = roomTypeFromHeaders || cookieRoomType

  const roomNameFromQuery =
    typeof ctx.query?.room === 'string'
      ? ctx.query.room
      : typeof ctx.query?.name === 'string'
      ? ctx.query.name
      : undefined

  let roomName: string = roomNameFromHeaders || roomNameFromQuery || ''

  if (!roomName && finalToken) {
    const decodedCookies = jwt.decode(finalToken)
    if (typeof decodedCookies === 'string') {
      roomName = decodedCookies
    } else {
      roomName = decodedCookies?.roomName
    }
  }

  // Проверяем project из query или из headers (для коротких ссылок)
  const projectMode = resolveProjectMode(
    (typeof ctx.query?.project === 'string' ? ctx.query.project : null) ?? projectFromHeaders,
  )

  if (isSamoletProjectMode(projectMode)) {
    try {
      const [companyBrandingResp, brandingMeetsResp, settingsResp] = await Promise.all([
        fetch(
          `${process.env.NEXT_PUBLIC_PLATFORM_ENDPOINT}/company-branding?room_name=${encodeURIComponent(
            roomName,
          )}`,
        ),
        fetch(`${process.env.NEXT_PUBLIC_HURMA_API}/branding_meets?roomName=${roomName}`),
        fetch(`${process.env.NEXT_PUBLIC_HURMA_API}/meet/setting/${ctx.query.name}`),
      ])

      // Выбор источника брендинга с приоритетом company-branding
      if (companyBrandingResp?.ok) {
        const companyBranding = await companyBrandingResp.json()
        if (companyBranding?.success && companyBranding?.data) {
          brandingData = {
            status: true,
            data: { list: [companyBranding.data] },
          } as Res<List>
        }
      }

      if (!brandingData && brandingMeetsResp) {
        const branding = await brandingMeetsResp.json()
        if (branding?.data?.list && branding.data.list.length > 0) {
          brandingData = branding as Res<List>
        } else if (branding?.data) {
          brandingData = {
            status: true,
            data: { list: [branding.data] },
          } as Res<List>
        }
      }

      const settings = await settingsResp?.json()
      const rawSettingsPayload = settings?.data || settings
      settingsData = (rawSettingsPayload?.settings || rawSettingsPayload) as ISettings
      promptData = (rawSettingsPayload?.prompt || '') as string | IPrompt
    } catch (error) {
      console.error(error)
    }
  } else if (isBitrixProjectMode(projectMode)) {
    const bitrixRoomName =
      (typeof ctx.query?.name === 'string' ? ctx.query.name : null) ||
      roomNameFromQuery ||
      roomNameFromHeaders

    try {
      if (bitrixRoomName) {
        const room = await fetchRoomByName(bitrixRoomName)

        meetCodes = {
          meet_id: room.id,
          company_id: null,
        }
        settingsData = mapMeetClientRoomToSettings(room)
        brandingData = {
          status: true,
          data: { list: [{ ...mapMeetClientRoomToBranding(room), meet_id: room.id }] },
        } as Res<List>
      } else {
        roomLookupError = 'not_found'
      }
    } catch (error) {
      console.error(error)
      roomLookupError = roomLookupErrorKindFromCode(getRoomLookupErrorCode(error)) ?? 'failed'
    }
  } else {
    const arrayKeyIndex = ctx.req?.rawHeaders.findIndex((elem: string) => elem === 'Host')
    if (arrayKeyIndex !== undefined && ctx.req?.rawHeaders) {
      const host = ctx.req.rawHeaders[arrayKeyIndex + 1]
      try {
        const checkMeet = await getCheckMeet(host, path)

        if (checkMeet.lookupError) {
          // Без комнаты не ходим за брендингом и meet_id: иначе можно войти в пустой звонок.
          roomLookupError = checkMeet.lookupError
        } else {
          meetCodes = {
            company_id: checkMeet?.data?.data?.company_id,
            meet_id: checkMeet?.data?.data?.meet_id,
          }

          try {
            const [companyBrandingResp, brandingMeetsResp, settingsResp, typeOfMeetResponse] =
              await Promise.all([
                roomName
                  ? fetch(
                      `${process.env.NEXT_PUBLIC_PLATFORM_ENDPOINT}/company-branding?room_name=${encodeURIComponent(
                        roomName,
                      )}`,
                    )
                  : Promise.resolve(null as unknown as Response),
                fetch(
                  `${process.env.NEXT_PUBLIC_PLATFORM_ENDPOINT}/branding_meets?meet_id=${checkMeet?.data?.data?.meet_id}`,
                ),
                fetch(
                  `${process.env.NEXT_PUBLIC_PLATFORM_ENDPOINT}/meet/setting/${checkMeet?.data?.data?.meet_id}`,
                ),
                fetch(`${process.env.NEXT_PUBLIC_PLATFORM_ENDPOINT}/types_meets/`),
              ])

            // Выбор брендинга с приоритетом company-branding
            if (companyBrandingResp && companyBrandingResp.ok) {
              const companyBranding = await companyBrandingResp.json()
              if (companyBranding?.success && companyBranding?.data) {
                brandingData = {
                  status: true,
                  data: { list: [companyBranding.data] },
                } as Res<List>
              }
            }

            if (!brandingData && brandingMeetsResp) {
              brandingData = await brandingMeetsResp.json()
            }

            const settingsResponse = await settingsResp?.json()
            const rawSettingsPayload = settingsResponse?.data || settingsResponse
            settingsData = rawSettingsPayload?.settings || rawSettingsPayload
            promptData = (rawSettingsPayload?.prompt || '') as string | IPrompt

            let typeOfMeet = (await typeOfMeetResponse?.json()) as ITypeOfMeetResponse
            let customTypeOfMeet: ICustomTypeOfMeetResponse | null = null

            if (typeOfMeet?.data?.types && typeOfMeet?.data?.types?.length > 0) {
              const customTypes = typeOfMeet?.data?.types?.find(
                (type) => type?.code_name === 'custom',
              )

              if (customTypes) {
                customTypeOfMeet = await fetch(
                  `${process.env.NEXT_PUBLIC_PLATFORM_ENDPOINT}/custom_types_meets?company_id=${checkMeet?.data?.data?.company_id}`,
                ).then(
                  async (res) =>
                    res?.ok && ((await res?.json()) as Promise<ICustomTypeOfMeetResponse>),
                )

                if (
                  customTypeOfMeet?.data?.sub_types &&
                  customTypeOfMeet?.data?.sub_types?.length > 0
                ) {
                  typeOfMeet.data.types = [
                    ...typeOfMeet?.data?.types?.map((type) => {
                      if (type?.code_name === 'custom') {
                        type.sub_types = customTypeOfMeet?.data?.sub_types ?? []
                      }

                      return type
                    }),
                  ]
                } else {
                  typeOfMeet.data.types = [
                    ...typeOfMeet?.data?.types?.filter((type) => type?.code_name !== 'custom'),
                  ]
                }
              }
            }

            typesOfMeet = typeOfMeet?.data?.types ?? []
          } catch (e) {
            if (!checkMeet?.data?.data?.meet_id) {
              console.error('meet_id in not defined')
            }

            console.error(e)
          }
        }
      } catch (error) {
        console.error(error)
        roomLookupError = roomLookupError ?? 'failed'
      }
    }
  }

  const finalBrandingData = brandingData?.data?.list?.[0]

  // Extract and combine user_name and user_phone
  const userName = typeof ctx.query?.user_name === 'string' ? ctx.query.user_name : null
  const userPhone = typeof ctx.query?.user_phone === 'string' ? ctx.query.user_phone : null
  let prefilledUsername: string | null = null
  
  if (userName && userPhone) {
    prefilledUsername = `${userName} ${userPhone}`
  } else if (userName) {
    prefilledUsername = userName
  } else if (userPhone) {
    prefilledUsername = userPhone
  }

  // Проверяем, является ли это голосовым режимом Федора
  const isFedorVoiceMode = finalRoomType === 'voiceAssistant' || finalRoomType === 'voiceAssistant2'
  const isMegaMeet = finalRoomType === 'meet'

  // Параметры скрытия кнопок
  // Для голосового режима Федора автоматически скрываем кнопки демонстрации экрана и чата
  // Для мегамитов автоматически скрываем кнопку выхода (но не для project=bitrix: там roomType=meet нужен агенту)
  const hiddenButtons = {
    screenShare: ctx.query?.hide_screen_share === 'true' || isFedorVoiceMode,
    chat: ctx.query?.hide_chat === 'true' || isFedorVoiceMode,
    leave: ctx.query?.hide_leave === 'true' || (isMegaMeet && !isBitrixProjectMode(projectMode)),
  }

  const data = {
    data: {
      ...initialBranding,
      ...finalBrandingData,
      settings: settingsData,
      ...getClientPromptProps(finalRoomType, promptData),
    },
    typesOfMeet: typesOfMeet ?? [],
    meetCodes,
    roomType: finalRoomType === 'null' ? null : finalRoomType || null,
    roomName,
    // Используем значения из headers (для коротких ссылок) или из query
    project: projectMode ?? (ctx.query?.project ?? projectFromHeaders ?? null),
    v: ctx.query?.v ?? vFromHeaders ?? null,
    referrer: cookieReferrer || null,
    prefilledUsername,
    hiddenButtons,
    roomLookupError,
  }

  return data
}

function MyApp({
  Component,
  pageProps,
  typesOfMeet,
  meetCodes,
  roomName,
  roomType,
  project,
  v,
  referrer,
  prefilledUsername,
  hiddenButtons,
  roomLookupError,
  data,
}: MyAppProps) {
  const { i18n } = useTranslation()
  const router = useRouter()

  useEffect(() => {
    if (router.query?.lang !== 'ru') {
      i18n.changeLanguage(router.query?.lang as string)
    }

    sessionStorage.setItem('settings', JSON.stringify(data?.settings ?? {}))
    sessionStorage.setItem('autoRecord', JSON.stringify(data?.settings?.auto_rec ?? false))
    sessionStorage.setItem('waitRoom', JSON.stringify(data?.settings?.wait_room ?? false))

    const userChoices = JSON.parse(localStorage.getItem('lk-user-choices') || '{}')
    const newUserChoices = {
      ...userChoices,
    }

    if (!data?.img_background_speaker && userChoices.currentBgEffect?.options?.imagePath) {
      newUserChoices.currentBgEffect = { type: 'none', options: {} }
    }

    localStorage.setItem('lk-user-choices', JSON.stringify(newUserChoices))
  }, [])

  const ToastIcon: ToastIcon = ({ type, theme }) => {
    switch (type) {
      case 'info':
        return <Bell className="toast-icon" />
      case 'error':
        return <Bell className="toast-icon" />
      case 'success':
        return <Bell className="toast-icon" />
      case 'warning':
        return <Bell className="toast-icon" />
      default:
        return null
    }
  }

  return (
    <>
      <Seo {...data} />

      <ErrorBoundary>
        <Provider store={store}>
          <MeetData
            typesOfMeet={typesOfMeet}
            meetCodes={meetCodes}
            roomName={roomName}
            roomType={roomType}
            project={project}
            v={v}
            referrer={referrer}
            prefilledUsername={prefilledUsername}
            hiddenButtons={hiddenButtons}
            {...data}
          >
            <Component {...pageProps} roomLookupError={roomLookupError} />
            <div id="modal-root" />
            <ToastContainer className="toast-container" icon={ToastIcon} autoClose={10000} />
          </MeetData>
        </Provider>
      </ErrorBoundary>
    </>
  )
}

export default MyApp
